import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Eye, EyeOff } from "lucide-react";
import { adminApi } from "../../lib/api";
import { toast } from "sonner";

export default function AdminBlogList() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/blog/admin/all");
      setList(r.data);
    } catch { toast.error("Failed to load posts"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const togglePublish = async (post) => {
    try {
      await adminApi.put(`/blog/${post.id}`, { published: !post.published });
      setList((l) => l.map((p) => (p.id === post.id ? { ...p, published: !p.published } : p)));
      toast.success(post.published ? "Unpublished" : "Published");
    } catch { toast.error("Failed to update"); }
  };

  const remove = async (post) => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    try {
      await adminApi.delete(`/blog/${post.id}`);
      setList((l) => l.filter((p) => p.id !== post.id));
      toast.success("Post deleted");
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <div className="space-y-8" data-testid="admin-blog-list">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="eyebrow mb-3">Editorial</div>
          <h1 className="font-serif text-4xl md:text-5xl">Journal posts.</h1>
        </div>
        <Link
          to="/admin/blog/new"
          data-testid="admin-blog-new"
          className="inline-flex items-center gap-2 px-6 py-3 bg-foreground text-background hover:bg-accent hover:text-accent-foreground transition-colors uppercase text-[11px] tracking-[0.25em]"
        >
          <Plus size={14} strokeWidth={1.5} /> New Post
        </Link>
      </div>

      {loading ? (
        <div className="text-foreground/50 text-center py-20">Loading…</div>
      ) : list.length === 0 ? (
        <div className="border border-border p-16 text-center text-foreground/60">No posts yet. Create your first one.</div>
      ) : (
        <ul className="space-y-3">
          {list.map((p) => (
            <li key={p.id} className="border border-border bg-background flex flex-col md:flex-row md:items-center gap-4 p-4 md:p-5">
              <div className="w-full md:w-24 aspect-[4/3] md:aspect-square overflow-hidden shrink-0 bg-secondary">
                {p.image && <img src={p.image} alt={p.title} className="h-full w-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="eyebrow text-foreground/55">{p.category}</span>
                  {p.featured && <span className="text-[9px] uppercase tracking-[0.22em] bg-accent text-accent-foreground px-2 py-0.5">Featured</span>}
                  {!p.published && <span className="text-[9px] uppercase tracking-[0.22em] border border-border px-2 py-0.5 text-foreground/55">Draft</span>}
                </div>
                <div className="font-serif text-xl md:text-2xl leading-tight">{p.title}</div>
                <div className="text-[11px] text-foreground/55 mt-1">{p.author} · {p.date}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => togglePublish(p)} title={p.published ? "Unpublish" : "Publish"} className="p-2.5 border border-border hover:border-foreground transition-colors">
                  {p.published ? <Eye size={14} strokeWidth={1.5} /> : <EyeOff size={14} strokeWidth={1.5} />}
                </button>
                <Link to={`/admin/blog/${p.id}`} title="Edit" className="p-2.5 border border-border hover:border-foreground transition-colors">
                  <Pencil size={14} strokeWidth={1.5} />
                </Link>
                <button onClick={() => remove(p)} title="Delete" className="p-2.5 border border-border hover:border-destructive hover:text-destructive transition-colors">
                  <Trash2 size={14} strokeWidth={1.5} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
